import { useState, useCallback } from "react";
import { useComplianceGeneratePresignedUrlsMutation } from "../../graphql/supergraphHooks";
import { clientContext } from "../../utils/graphql_utils";
import { uploadFilesToS3, getUrlGeneratorInput } from "../../utils/s3UploadClient";
/**
 * A react hook which returns a function that takes in a list of files and uploads them to
 * the S3 bucket.
 * The function returns a Promise object which will resolve to an array of
 * UploadResponseType instances. Each contain the Response and objectKey for the uploaded file.
 * The objectKey can be used to reference the file in the S3 bucket.
 *
 * @param options - Optional configuration options
 * @param options.caseId - Optional case ID to associate with the uploaded files
 */
export default function useUploadFilesToS3(options?: { caseId?: number }) {
  const [loading, setLoading] = useState(false);
  // Uploads to S3 bucket require a pre-signed URL. This mutation will take care of
  // generating the pre-signed URL and generating an object key to reference the file.
  const [getPresignedUrlsForUpload] = useComplianceGeneratePresignedUrlsMutation({
    context: clientContext(),
  });

  const uploadFiles = useCallback(
    (files: File[], retryCount = 3) => {
      setLoading(true);
      const urlGeneratorInput = getUrlGeneratorInput(files);
      return getPresignedUrlsForUpload({
        context: clientContext(),
        variables: {
          urlGeneratorInput,
          caseId: options?.caseId,
        },
      })
        .then((urlResponse) => {
          const presignedObjs = urlResponse.data?.complianceGeneratePresignedUrls || [];
          const filesToUpload = files.map((file, index) => {
            return {
              generatedUrlObj: presignedObjs[index],
              file,
            };
          });
          return uploadFilesToS3(filesToUpload, retryCount);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [getPresignedUrlsForUpload, options?.caseId],
  );

  return { uploadFilesToS3: uploadFiles, loading };
}
